import axios from 'axios';

import { UserRole } from '@tallo/user';

import { AccessToken } from '../model/access-token.interface';
import { ActivateRenterAccountWithGoogleDto } from '../model/activate-renter-account-with-google.dto';
import { CreateUserDto } from '../model/create-user.dto';
import { PasswordSetupDto } from '../model/password-setup.dto';
import { SetupNewPasswordDto } from '../model/setup-new-password.dto';
import { SignInWithCredsDto } from '../model/sign-in-with-creds.dto';
import { UserSignedInWithGoogleDto } from '../model/user-signed-in-with-google.dto';

class AuthService {
  async signInWithCredentials(params: SignInWithCredsDto): Promise<AccessToken> {
    const { data } = await axios.post('/auth/sign-in-credentials', {
      username: params.email,
      password: params.password,
      requiredRole: params.requiredRole,
    });

    return data;
  }

  async sendPasswordResetLink(email: string, forRole: UserRole): Promise<void> {
    await axios.post('auth/send-password-reset-link', { email, forRole });
  }

  async confirmPasswordReset(passwordSetupDto: PasswordSetupDto): Promise<AccessToken> {
    const { data } = await axios.post('auth/confirm-password-reset', passwordSetupDto);
    return data;
  }

  async requestOneTimeSignInCode(email: string): Promise<void> {
    await axios.post('/auth/request-one-time-sign-in-code', { email });
  }

  async signInWithOneTimeCode(email: string, code: number): Promise<AccessToken> {
    const { data } = await axios.post('/auth/sign-in-with-one-time-code', { email, code: code.toString() });
    return data;
  }

  async investorSignUpWithCreds(userData: CreateUserDto): Promise<{ userId: string }> {
    const { data } = await axios.post('/auth/investor-sign-up-credentials', userData);
    return data;
  }

  async investorConfirmEmail(userId: string, code: number): Promise<{ companyId: string; accessToken: string }> {
    const { data } = await axios.patch('/auth/investor-confirm-email', { userId, code: code.toString() });
    return data;
  }

  async investorResendEmailConfirmationCode(userId: string): Promise<void> {
    await axios.post('/auth/investor-resend-email-confirmation-code', { userId });
  }

  async investorSignInWithGoogle(googleJwt: string): Promise<UserSignedInWithGoogleDto> {
    const { data } = await axios.post<UserSignedInWithGoogleDto>('/auth/investor-sign-in-google', { googleJwt });
    return data;
  }

  async investorSignUpWithGoogle(googleJwt: string): Promise<UserSignedInWithGoogleDto> {
    const { data } = await axios.post<UserSignedInWithGoogleDto>('/auth/investor-sign-up-google', { googleJwt });
    return data;
  }

  async renterSignInWithGoogle(googleJwt: string): Promise<AccessToken> {
    const { data } = await axios.post<AccessToken>('/auth/renter-sign-in-google', { googleJwt });
    return data;
  }

  async renterActivateWithGoogle(accountActivationDto: ActivateRenterAccountWithGoogleDto): Promise<AccessToken> {
    const { data } = await axios.post<AccessToken>('/auth/renter-activate-google', accountActivationDto);
    return data;
  }

  async renterActivateWithCreds(passwordSetupDto: SetupNewPasswordDto): Promise<AccessToken> {
    const { data } = await axios.post<AccessToken>('/auth/renter-activate-creds', passwordSetupDto);
    return data;
  }
}

export default new AuthService();
